(() => {
  if (window.__WWT_CODE_GATED_GWP_INIT__) return;
  window.__WWT_CODE_GATED_GWP_INIT__ = true;

  const DEBUG = false;
  const log = (...args) => DEBUG && console.log("[WWTCodeGWP]", ...args);
  const warn = (...args) => DEBUG && console.warn("[WWTCodeGWP]", ...args);

  const PROMO_PREFIX = "WWT-";
  const GIFT_VARIANT_ID = "56444556050598";

  const DISCOUNT_BOX_SELECTOR = ".discount-box";
  const DISCOUNT_FORM_SELECTOR = ".discount-box-form";
  const DISCOUNT_INPUT_SELECTOR = "#slidecart_discount_code, input[name='discount_code']";

  const CART_ENDPOINTS = {
    get: "/cart.js",
    add: "/cart/add.js",
    change: "/cart/change.js",
    update: "/cart/update.js",
  };

  const ENFORCE_DEBOUNCE_MS = 300;
  const CONTROL_LOCK_DEBOUNCE_MS = 150;
  const VERIFY_AFTER_CHANGE_MS = 650;
  const DISCOUNT_VERIFY_TRIES = 10;
  const DISCOUNT_VERIFY_DELAY = 450;

  let promoInFlight = false;
  let enforceTimeout = null;
  let controlLockTimeout = null;
  let controlLockInProgress = false;
  let controlLockPending = false;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // --------------------
  // Fetch/cart helpers
  // --------------------
  async function fetchJson(url, options = {}) {
    const res = await fetch(url, {
      credentials: "same-origin",
      ...options,
    });

    let data = null;

    try {
      data = await res.json();
    } catch {
      // ignore non-json responses
    }

    return { res, data };
  }

  async function getCart() {
    const { res, data } = await fetchJson(CART_ENDPOINTS.get);

    if (!res.ok) {
      throw new Error(`getCart failed: HTTP ${res.status}`);
    }

    return data;
  }

  async function updateDiscountCodes(codes) {
    const uniqueCodes = [...new Set((codes || []).map(normalizeCode).filter(Boolean))];

    const { res, data } = await fetchJson(CART_ENDPOINTS.update, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        discount: uniqueCodes.join(","),
      }),
    });

    if (DEBUG) {
      console.groupCollapsed("[WWTCodeGWP] /cart/update.js discount response");
      console.log("Attempted codes:", uniqueCodes);
      console.log("ok:", res.ok);
      console.log("status:", res.status);
      console.log("Returned data:", data);
      console.groupEnd();
    }

    return { res, data };
  }

  async function applyWwtDiscountCode(code, cartToPreserve = null) {
    const normalizedCode = normalizeCode(code);
    const cart = cartToPreserve || (await getCart());
    const nonWwtCodes = getNonWwtDiscountCodes(cart);

    log("Applying WWT BXGY code", {
      code: normalizedCode,
      preservingCodes: nonWwtCodes,
    });

    return updateDiscountCodes([...nonWwtCodes, normalizedCode]);
  }

  async function clearWwtDiscountCodes(cartToUse = null) {
    const cart = cartToUse || (await getCart());

    if (!hasAnyWwtDiscountCode(cart)) return false;

    const nonWwtCodes = getNonWwtDiscountCodes(cart);

    log("Clearing WWT codes, preserving non-WWT codes", {
      nonWwtCodes,
    });

    const { res } = await updateDiscountCodes(nonWwtCodes);
    return res.ok;
  }

  async function addGiftVariant(code) {
    const normalizedCode = normalizeCode(code);

    log("Adding WWT BXGY gift variant", {
      code: normalizedCode,
      GIFT_VARIANT_ID,
    });

    const { res, data } = await fetchJson(CART_ENDPOINTS.add, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity: 1,
            properties: {
              _wwt_code_gated_gift: "true",
              _wwt_discount_prefix: PROMO_PREFIX,
              _wwt_discount_code: normalizedCode,
            },
          },
        ],
      }),
    });

    if (DEBUG) {
      console.groupCollapsed("[WWTCodeGWP] /cart/add.js gift response");
      console.log("ok:", res.ok);
      console.log("status:", res.status);
      console.log("data:", data);
      console.groupEnd();
    }

    return { res, data };
  }

  async function setLineQuantity(lineKey, quantity) {
    log("Setting line quantity", { lineKey, quantity });

    const { res, data } = await fetchJson(CART_ENDPOINTS.change, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: lineKey,
        quantity,
      }),
    });

    if (DEBUG) {
      console.groupCollapsed("[WWTCodeGWP] /cart/change.js response");
      console.log("lineKey:", lineKey);
      console.log("quantity:", quantity);
      console.log("ok:", res.ok);
      console.log("status:", res.status);
      console.log("data:", data);
      console.groupEnd();
    }

    return { res, data };
  }

  // --------------------
  // Debug helpers
  // --------------------
  function simplifyMoney(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  function simplifyLine(item) {
    return {
      title: item.title,
      product_title: item.product_title,
      variant_title: item.variant_title,
      variant_id: item.variant_id,
      key: item.key,
      quantity: item.quantity,
      price: item.price,
      original_line_price: item.original_line_price,
      final_line_price: item.final_line_price,
      original_line_price_display: simplifyMoney(item.original_line_price),
      final_line_price_display: simplifyMoney(item.final_line_price),
      line_is_discounted: giftLineIsDiscounted(item),
      properties: item.properties,
      discount_allocations: item.discount_allocations,
    };
  }

  function simplifyCart(cart) {
    return {
      token: cart.token,
      item_count: cart.item_count,
      total_price: cart.total_price,
      original_total_price: cart.original_total_price,
      total_discount: cart.total_discount,
      total_price_display: simplifyMoney(cart.total_price),
      original_total_price_display: simplifyMoney(cart.original_total_price),
      total_discount_display: simplifyMoney(cart.total_discount),
      discount_codes: cart.discount_codes,
      cart_level_discount_applications: cart.cart_level_discount_applications,
      items: (cart.items || []).map(simplifyLine),
    };
  }

  function debugCart(label, cart) {
    if (!DEBUG) return;

    console.groupCollapsed(`[WWTCodeGWP] ${label}`);
    console.log("Simplified cart:", simplifyCart(cart));
    console.log("Raw cart:", cart);
    console.groupEnd();
  }

  function debugBxgyValidation(cart, expectedCode = "") {
    if (!DEBUG) return;

    const code = normalizeCode(expectedCode);
    const matchingGift = getPrimaryCodeOwnedGiftLineForCode(cart, code);

    console.groupCollapsed("[WWTCodeGWP] BXGY validation details");
    console.log("Expected code:", code);
    console.log("discount_codes:", cart.discount_codes);
    console.log("cart_level_discount_applications:", cart.cart_level_discount_applications);
    console.log("getValidWwtDiscountCode:", getValidWwtDiscountCode(cart));
    console.log("hasValidWwtDiscountCode:", hasValidWwtDiscountCode(cart, code));
    console.log("matchingGiftLine:", matchingGift ? simplifyLine(matchingGift) : null);
    console.log("giftLineIsDiscounted:", matchingGift ? giftLineIsDiscounted(matchingGift) : false);
    console.log("hasValidBxgyGiftState:", hasValidBxgyGiftState(cart, code));
    console.groupEnd();
  }

  // --------------------
  // Promo helpers
  // --------------------
  function normalizeCode(code) {
    return String(code || "").trim().toUpperCase();
  }

  function isWwtCode(code) {
    return normalizeCode(code).startsWith(PROMO_PREFIX);
  }

  function getCartDiscountCodes(cart) {
    return Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
  }

  function getAppliedDiscountCodeStrings(cart) {
    return getCartDiscountCodes(cart)
      .map((discount) => normalizeCode(discount?.code))
      .filter(Boolean);
  }

  function getNonWwtDiscountCodes(cart) {
    return getAppliedDiscountCodeStrings(cart).filter((code) => !isWwtCode(code));
  }

  function hasAnyWwtDiscountCode(cart) {
    return getAppliedDiscountCodeStrings(cart).some(isWwtCode);
  }

  function getValidWwtDiscountCode(cart) {
    const validDiscount = getCartDiscountCodes(cart).find((discount) => {
      const discountCode = normalizeCode(discount?.code);
      return discount?.applicable === true && isWwtCode(discountCode);
    });

    return normalizeCode(validDiscount?.code);
  }

  function hasValidWwtDiscountCode(cart, expectedCode = "") {
    const normalizedExpectedCode = normalizeCode(expectedCode);
    const validCode = getValidWwtDiscountCode(cart);

    if (!validCode) return false;

    if (normalizedExpectedCode) {
      return validCode === normalizedExpectedCode;
    }

    return true;
  }

  function hasAnyWwtDiscountCodeAppliedButInvalid(cart) {
    return getCartDiscountCodes(cart).some((discount) => {
      const discountCode = normalizeCode(discount?.code);
      return isWwtCode(discountCode) && discount?.applicable !== true;
    });
  }

  function isGiftVariantLine(item) {
    return String(item.variant_id) === String(GIFT_VARIANT_ID);
  }

  function getAllGiftVariantLines(cart) {
    return (cart.items || []).filter(isGiftVariantLine);
  }

  function isCodeGatedGiftLine(item) {
    return (
      isGiftVariantLine(item) &&
      item.properties &&
      item.properties._wwt_code_gated_gift === "true"
    );
  }

  function getCodeGatedGiftLines(cart) {
    return (cart.items || []).filter(isCodeGatedGiftLine);
  }

  function getPrimaryCodeOwnedGiftLineForCode(cart, code) {
    const normalizedCode = normalizeCode(code);

    return getCodeGatedGiftLines(cart).find((item) => {
      return normalizeCode(item.properties?._wwt_discount_code) === normalizedCode;
    });
  }

  function hasQualifyingProduct(cart) {
    return (cart.items || []).some((item) => {
      // Any non-WWT-gift product qualifies.
      // Because this variant is gift-only, exclude all lines with this variant ID,
      // including URL-added lines without hidden properties.
      return !isGiftVariantLine(item);
    });
  }

  function giftLineIsDiscounted(item) {
    if (!item) return false;

    const original = Number(item.original_line_price || 0);
    const final = Number(item.final_line_price || 0);

    return original > 0 && final < original;
  }

  function hasValidBxgyGiftState(cart, expectedCode = "") {
    const normalizedCode = normalizeCode(expectedCode);
    const validCode = getValidWwtDiscountCode(cart);

    if (!validCode) return false;

    if (normalizedCode && validCode !== normalizedCode) return false;

    if (!hasQualifyingProduct(cart)) return false;

    const giftLine = getPrimaryCodeOwnedGiftLineForCode(cart, validCode);

    if (!giftLine) return false;

    return giftLineIsDiscounted(giftLine);
  }

  async function removeAllGiftVariantLines(cart) {
    const giftLines = getAllGiftVariantLines(cart);

    if (!giftLines.length) return false;

    log("Removing all WWT gift variant lines", {
      count: giftLines.length,
    });

    for (const item of giftLines) {
      await setLineQuantity(item.key, 0);
    }

    return true;
  }

  async function prepareGiftLineForBxgyCode(cart, code) {
    const normalizedCode = normalizeCode(code);
    const allGiftLines = getAllGiftVariantLines(cart);
    const codeGatedGiftLines = getCodeGatedGiftLines(cart);

    let didChange = false;

    if (!normalizedCode) {
      if (allGiftLines.length) {
        await removeAllGiftVariantLines(cart);
        return true;
      }

      return false;
    }

    const matchingCodeOwnedLines = codeGatedGiftLines.filter((item) => {
      return normalizeCode(item.properties?._wwt_discount_code) === normalizedCode;
    });

    const primaryGiftLine = matchingCodeOwnedLines[0];

    if (!primaryGiftLine) {
      // Remove manual URL-added lines, wrong-code lines, and stale duplicates.
      // Then add the official code-owned gift line before applying BXGY.
      for (const item of allGiftLines) {
        await setLineQuantity(item.key, 0);
        didChange = true;
      }

      const { res } = await addGiftVariant(normalizedCode);

      if (!res.ok) {
        throw new Error(`addGiftVariant failed: HTTP ${res.status}`);
      }

      return true;
    }

    if (Number(primaryGiftLine.quantity || 0) !== 1) {
      await setLineQuantity(primaryGiftLine.key, 1);
      didChange = true;
    }

    for (const item of allGiftLines) {
      if (item.key === primaryGiftLine.key) continue;

      await setLineQuantity(item.key, 0);
      didChange = true;
    }

    return didChange;
  }

  async function ensureGiftLineReadyForBxgy(code) {
    let cart = await getCart();
    debugCart("Cart before preparing BXGY gift line", cart);

    const didChange = await prepareGiftLineForBxgyCode(cart, code);

    cart = await getCart();
    debugCart("Cart after preparing BXGY gift line", cart);

    if (didChange) {
      queueControlLock(100);
      queueControlLock(500);
    }

    return cart;
  }

  async function waitForValidBxgyGiftState(code) {
    for (let i = 0; i < DISCOUNT_VERIFY_TRIES; i++) {
      const cart = await getCart();

      if (DEBUG) {
        console.groupCollapsed(`[WWTCodeGWP] BXGY validation attempt ${i + 1}`);
        debugCart("Cart during BXGY validation attempt", cart);
        debugBxgyValidation(cart, code);
        console.groupEnd();
      }

      if (hasValidBxgyGiftState(cart, code)) {
        log("Valid WWT BXGY state found", { code });
        return cart;
      }

      await sleep(DISCOUNT_VERIFY_DELAY);
    }

    const finalCart = await getCart();
    debugCart("Final cart after BXGY validation retries", finalCart);
    debugBxgyValidation(finalCart, code);

    return finalCart;
  }

  // --------------------
  // UI helpers
  // --------------------
  function getDiscountBox(form) {
    if (!form) return null;

    return form.closest(DISCOUNT_BOX_SELECTOR) || form.parentElement;
  }

  function getDiscountFormMessageEl(form) {
    if (!form) return null;

    const box = getDiscountBox(form);
    if (!box) return null;

    let message = box.querySelector(".wwt-code-gwp-message");

    if (!message) {
      message = document.createElement("div");
      message.className = "wwt-code-gwp-message";
      message.setAttribute("role", "status");
      message.setAttribute("aria-live", "polite");
    }

    // Keep message outside the form, directly beneath the input/button row.
    if (message.parentElement !== box || message.previousElementSibling !== form) {
      form.insertAdjacentElement("afterend", message);
    }

    return message;
  }

  function setMessage(form, text, type = "info") {
    const message = getDiscountFormMessageEl(form);

    if (!message) return;

    message.textContent = text || "";
    message.dataset.type = type;
  }

  function setFormBusy(form, busy) {
    if (!form) return;

    const box = getDiscountBox(form);
    const button = form.querySelector("button[type='submit'], button");
    const input = form.querySelector(DISCOUNT_INPUT_SELECTOR);

    if (box) {
      box.classList.toggle("wwt-code-gwp-is-loading", Boolean(busy));
    }

    if (input) {
      input.readOnly = Boolean(busy);
      input.setAttribute("aria-busy", busy ? "true" : "false");
    }

    if (button) {
      if (!button.dataset.wwtOriginalHtml) {
        button.dataset.wwtOriginalHtml = button.innerHTML;
      }

      button.disabled = Boolean(busy);
      button.setAttribute("aria-busy", busy ? "true" : "false");

      if (busy) {
        button.innerHTML = `
          <span class="wwt-code-gwp-button-content">
            <span class="wwt-code-gwp-spinner" aria-hidden="true"></span>
            <span>Applying...</span>
          </span>
        `;
      } else {
        button.innerHTML = button.dataset.wwtOriginalHtml || "Apply";
      }
    }
  }

  function setDisabled(el, disabled) {
    if (!el) return;

    if ("disabled" in el && el.disabled !== disabled) {
      el.disabled = disabled;
    }

    if (disabled) {
      if (el.getAttribute("aria-disabled") !== "true") {
        el.setAttribute("aria-disabled", "true");
      }

      if (el.tabIndex !== -1) {
        el.tabIndex = -1;
      }
    } else {
      el.removeAttribute("aria-disabled");
    }
  }

  function lockGiftRowControls(row) {
    if (!row) return;

    if (row.getAttribute("data-wwt-code-gwp-locked") !== "true") {
      row.setAttribute("data-wwt-code-gwp-locked", "true");
    }

    const qtyButtons = row.querySelectorAll(
      '.quantity-selector button, button[aria-label="decrease quantity"], button[aria-label="increase quantity"]'
    );

    qtyButtons.forEach((button) => {
      setDisabled(button, true);
    });

    const qtyInputs = row.querySelectorAll(
      '.quantity-selector input[type="text"], .quantity-selector input[type="number"]'
    );

    qtyInputs.forEach((input) => {
      if (!input.readOnly) {
        input.readOnly = true;
      }

      if (input.value !== "1") {
        input.value = "1";
      }

      if (input.getAttribute("aria-readonly") !== "true") {
        input.setAttribute("aria-readonly", "true");
      }

      if (input.getAttribute("aria-disabled") !== "true") {
        input.setAttribute("aria-disabled", "true");
      }

      if (input.tabIndex !== -1) {
        input.tabIndex = -1;
      }
    });

    const removeButtons = row.querySelectorAll(
      '.remove, button[aria-label^="remove"], button[aria-label*="remove"]'
    );

    removeButtons.forEach((button) => {
      setDisabled(button, true);
    });
  }

  async function lockGiftLineControls() {
    if (controlLockInProgress) {
      controlLockPending = true;
      return;
    }

    controlLockInProgress = true;
    controlLockPending = false;

    try {
      const cart = await getCart();
      const giftLineKeys = new Set(
        getAllGiftVariantLines(cart).map((item) => String(item.key))
      );

      document.querySelectorAll('[data-wwt-code-gwp-locked="true"]').forEach((row) => {
        const rowKey = String(row.getAttribute("data-line-item-key") || "");

        if (!giftLineKeys.has(rowKey)) {
          row.removeAttribute("data-wwt-code-gwp-locked");
        }
      });

      document.querySelectorAll("[data-line-item-key]").forEach((row) => {
        const rowKey = String(row.getAttribute("data-line-item-key") || "");
        const shouldLock = giftLineKeys.has(rowKey);

        if (!shouldLock) return;

        lockGiftRowControls(row);
      });
    } catch (e) {
      warn("Could not lock WWT gift controls", e);
    } finally {
      controlLockInProgress = false;

      if (controlLockPending) {
        controlLockPending = false;
        scheduleControlLock(50);
      }
    }
  }

  function scheduleControlLock(delay = CONTROL_LOCK_DEBOUNCE_MS) {
    clearTimeout(controlLockTimeout);

    controlLockTimeout = setTimeout(() => {
      lockGiftLineControls();
    }, delay);
  }

  function queueControlLock(delay = CONTROL_LOCK_DEBOUNCE_MS) {
    setTimeout(() => {
      lockGiftLineControls();
    }, delay);
  }

  function refreshSlideCart() {
    try {
      window.SLIDECART_UPDATE?.();
    } catch (e) {
      warn("SLIDECART_UPDATE failed", e);
    }

    try {
      document.dispatchEvent(new CustomEvent("wwt-code-gwp:cart-updated"));
    } catch {
      // ignore
    }

    queueControlLock(100);
    queueControlLock(500);
    queueControlLock(1000);
  }

  // --------------------
  // Main submit handler
  // --------------------
  async function handleWwtDiscountSubmit(event) {
    const form = event.target?.closest?.(DISCOUNT_FORM_SELECTOR);

    if (!form) return;

    const input = form.querySelector(DISCOUNT_INPUT_SELECTOR);
    const rawCode = input?.value;
    const code = normalizeCode(rawCode);

    if (DEBUG) {
      console.groupCollapsed("[WWTCodeGWP] Discount submit captured");
      console.log("Event target:", event.target);
      console.log("Form:", form);
      console.log("Input:", input);
      console.log("Raw code:", rawCode);
      console.log("Normalized code:", code);
      console.log("Is WWT code?", isWwtCode(code));
      console.groupEnd();
    }

    // Let AMP handle all non-WWT discount codes normally.
    if (!isWwtCode(code)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    promoInFlight = true;
    setFormBusy(form, true);
    setMessage(form, "Adding your promo item...", "info");

    try {
      let cart = await getCart();
      debugCart("Cart at start of WWT submit", cart);

      if (!hasQualifyingProduct(cart)) {
        await removeAllGiftVariantLines(cart);
        await clearWwtDiscountCodes(cart);
        refreshSlideCart();
        setMessage(form, "Add any product to your cart to use this code.", "error");
        return;
      }

      // BXGY requires the Y item to be present before the code can validate.
      // This temporary gift line is allowed only while promoInFlight is true.
      console.log("[WWTCodeGWP] Step 1: preparing BXGY gift line");
      cart = await ensureGiftLineReadyForBxgy(code);

      console.log("[WWTCodeGWP] Step 2: applying WWT BXGY code via /cart/update.js");
      setMessage(form, "Applying code...", "info");

      const { res: discountRes, data: discountData } = await applyWwtDiscountCode(code, cart);

      console.log("[WWTCodeGWP] Step 3: discount apply response received", {
        ok: discountRes.ok,
        status: discountRes.status,
        discountData,
      });

      if (!discountRes.ok) {
        cart = await getCart();
        debugCart("Cart after failed /cart/update.js response", cart);

        await removeAllGiftVariantLines(cart);
        await clearWwtDiscountCodes(cart);
        refreshSlideCart();
        setMessage(form, "That code could not be applied.", "error");
        return;
      }

      setMessage(form, "Validating code...", "info");
      cart = await waitForValidBxgyGiftState(code);

      console.log("[WWTCodeGWP] Step 4: final BXGY validation result", {
        hasValidBxgyGiftState: hasValidBxgyGiftState(cart, code),
        discount_codes: cart.discount_codes,
      });

      if (!hasValidBxgyGiftState(cart, code)) {
        console.warn("[WWTCodeGWP] WWT BXGY state is not valid. Removing gift.");
        debugBxgyValidation(cart, code);

        await removeAllGiftVariantLines(cart);
        await clearWwtDiscountCodes(cart);
        refreshSlideCart();

        if (hasAnyWwtDiscountCodeAppliedButInvalid(cart)) {
          setMessage(form, "That code is not valid or has already been used.", "error");
        } else {
          setMessage(form, "That code could not be applied.", "error");
        }

        return;
      }

      // Final cleanup: exactly one official gift line at qty 1.
      await prepareGiftLineForBxgyCode(cart, code);

      cart = await getCart();
      debugCart("Cart after successful WWT BXGY unlock", cart);

      refreshSlideCart();
      queueControlLock(50);
      queueControlLock(300);
      setMessage(form, "Promo item unlocked!", "success");

      console.log("[WWTCodeGWP] Success: WWT BXGY code applied and gift discounted.");
    } catch (e) {
      warn("WWT BXGY submit failed", e);

      try {
        const cart = await getCart();
        debugCart("Cart before catch cleanup", cart);
        await removeAllGiftVariantLines(cart);
        await clearWwtDiscountCodes(cart);
        refreshSlideCart();
      } catch (cleanupError) {
        warn("WWT cleanup failed", cleanupError);
      }

      setMessage(form, "Something went wrong applying this code. Please try again.", "error");
    } finally {
      promoInFlight = false;
      setFormBusy(form, false);
    }
  }

  // --------------------
  // Enforcement
  // --------------------
  async function enforceGiftCannotExistWithoutValidCode(reason = "unknown") {
    if (promoInFlight) {
      log("Skipping enforcement because promo is in flight", { reason });
      return;
    }

    try {
      let cart = await getCart();
      const giftLines = getAllGiftVariantLines(cart);
      const validCode = getValidWwtDiscountCode(cart);
      const hasQualifier = hasQualifyingProduct(cart);

      debugCart(`Enforcement cart check: ${reason}`, cart);
      debugBxgyValidation(cart, validCode);

      // No valid WWT code or no qualifying product = no WWT gift allowed.
      if (!validCode || !hasQualifier) {
        if (giftLines.length) {
          log("Removing WWT gift during enforcement", {
            reason,
            hasQualifier,
            validCode,
            discount_codes: cart.discount_codes,
          });

          await removeAllGiftVariantLines(cart);
          refreshSlideCart();
        }

        return;
      }

      // Valid code exists, but the gift must still be exactly qty 1 and discounted.
      const didSync = await prepareGiftLineForBxgyCode(cart, validCode);

      if (didSync) {
        cart = await getCart();

        // After any gift-line sync, re-apply the BXGY code so Shopify recalculates the benefit.
        await applyWwtDiscountCode(validCode, cart);
        cart = await waitForValidBxgyGiftState(validCode);

        debugCart("Cart after enforcement sync", cart);
        refreshSlideCart();
        return;
      }

      if (!hasValidBxgyGiftState(cart, validCode)) {
        log("Removing WWT gift because BXGY state is no longer valid", {
          reason,
          validCode,
          discount_codes: cart.discount_codes,
        });

        await removeAllGiftVariantLines(cart);
        refreshSlideCart();
        return;
      }

      queueControlLock(50);
    } catch (e) {
      warn("WWT enforcement failed", e);
    }
  }

  function scheduleEnforcement(reason = "debounced", delay = ENFORCE_DEBOUNCE_MS) {
    clearTimeout(enforceTimeout);

    enforceTimeout = setTimeout(() => {
      enforceGiftCannotExistWithoutValidCode(reason);
    }, delay);
  }

  function getObserverTarget() {
    return (
      document.querySelector("#slidecarthq") ||
      document.querySelector(".slidecarthq") ||
      document.body
    );
  }

  function startObserver() {
    const target = getObserverTarget();

    const observer = new MutationObserver(() => {
      scheduleControlLock();
      scheduleEnforcement("mutation");
    });

    observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-line-item-key", "data-variant-id", "class"],
    });

    log("Observer started", {
      target: target === document.body ? "body" : "slidecart-root",
    });
  }

  // --------------------
  // Checkout protection
  // --------------------
  function isCheckoutTrigger(target) {
    if (!(target instanceof Element)) return false;

    const trigger = target.closest("a, button, input[type='submit']");

    if (!trigger) return false;

    const href = trigger.getAttribute("href") || "";
    const name = trigger.getAttribute("name") || "";
    const text = trigger.textContent || "";
    const classes = String(trigger.className || "");

    const haystack = `${href} ${name} ${text} ${classes}`.toLowerCase();

    return haystack.includes("/checkout") || haystack.includes("checkout");
  }

  async function handleCheckoutClick(event) {
    const target = event.target;

    if (!isCheckoutTrigger(target)) return;

    if (promoInFlight) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      const form = document.querySelector(DISCOUNT_FORM_SELECTOR);
      setMessage(form, "Please wait while your code finishes applying.", "info");
      return;
    }

    const cart = await getCart();
    const giftLines = getAllGiftVariantLines(cart);
    const validCode = getValidWwtDiscountCode(cart);

    if (!giftLines.length && !validCode) return;

    if (hasQualifyingProduct(cart) && validCode) {
      const didSync = await prepareGiftLineForBxgyCode(cart, validCode);
      let finalCart = await getCart();

      if (didSync || !hasValidBxgyGiftState(finalCart, validCode)) {
        await applyWwtDiscountCode(validCode, finalCart);
        finalCart = await waitForValidBxgyGiftState(validCode);
      }

      if (hasValidBxgyGiftState(finalCart, validCode)) {
        refreshSlideCart();
        return;
      }
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    await removeAllGiftVariantLines(cart);
    await clearWwtDiscountCodes(cart);
    refreshSlideCart();

    const form = document.querySelector(DISCOUNT_FORM_SELECTOR);
    setMessage(form, "The promo item was removed because the code is not valid.", "error");
  }

  // --------------------
  // Init
  // --------------------
  function init() {
    document.addEventListener("submit", handleWwtDiscountSubmit, true);
    document.addEventListener("click", handleCheckoutClick, true);

    startObserver();

    setTimeout(() => {
      enforceGiftCannotExistWithoutValidCode("init");
      queueControlLock(100);
      queueControlLock(500);
    }, VERIFY_AFTER_CHANGE_MS);

    log("WWT BXGY code-gated GWP script initialized");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init(); 
  }
})();